import axiosInstance from "@/setup/axios/axiosInstance";

// Document interfaces
export interface Document {
  id: string;
  content_type: number;
  object_id: number;
  content_type_name: string;
  name: string;
  file?: string;
  file_size: number;
  mime_type: string;
  is_directory: boolean;
  parent?: string;
  path: string;
  uploaded_by?: string;
  uploaded_by_name?: string;
  created_at: string;
  updated_at: string;
  file_url?: string;
  is_file: boolean;
}

export interface CreateDocumentRequest {
  content_type_id: number;
  object_id: string;
  name: string;
  file?: File;
  mime_type?: string;
  is_directory?: boolean;
  parent_id?: string;
  document_type_id?: string;
}

export interface UpdateDocumentRequest {
  name?: string;
  file?: File;
  mime_type?: string;
}

export interface CreateFolderRequest {
  content_type_id: number;
  object_id: string;
  name: string;
  parent_id?: string;
}

// ContentType interfaces
export interface ContentType {
  id: number;
  app_label: string;
  model: string;
  name: string;
}

const contentTypeIdCache = new Map<string, number>();
const contentTypeIdInFlight = new Map<string, Promise<number>>();

// Get ContentType ID for a model
export const getContentTypeId = async (modelName: string): Promise<number> => {
  const normalizedModelName = String(modelName || '').trim().toLowerCase();
  if (!normalizedModelName) {
    throw new Error('Model name is required');
  }

  if (contentTypeIdCache.has(normalizedModelName)) {
    return contentTypeIdCache.get(normalizedModelName) as number;
  }

  const existingRequest = contentTypeIdInFlight.get(normalizedModelName);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
  try {
    const response = await axiosInstance.get(`documents/contenttypes/by-model/${normalizedModelName}/`);
    const id = Number(response.data.id);
    if (Number.isFinite(id)) {
      contentTypeIdCache.set(normalizedModelName, id);
      return id;
    }

    throw new Error(`Invalid content type id for model: ${normalizedModelName}`);
  } catch (error) {
    console.error(`Error getting content type for model ${normalizedModelName}:`, error);
    
    // Fallback: try to get from all content types
    try {
      const allContentTypes = await getAllContentTypes();
      const contentType = allContentTypes.find(ct => 
        ct.model.toLowerCase() === normalizedModelName || 
        ct.app_label.toLowerCase() === normalizedModelName
      );
      
      if (contentType) {
        console.log(`Found content type for ${normalizedModelName}:`, contentType);
        contentTypeIdCache.set(normalizedModelName, contentType.id);
        return contentType.id;
      }
      
      // If still not found, use hardcoded values as last resort
      const hardcodedContentTypes: { [key: string]: number } = {
        'order': 34,  // orders.order
        'customer': 15,  // customers.customer
        'company': 13,  // companies.company
        'employee': 20,  // employees.employee
      };
      
      if (hardcodedContentTypes[normalizedModelName]) {
        console.log(`Using hardcoded content type for ${normalizedModelName}:`, hardcodedContentTypes[normalizedModelName]);
        const hardcodedId = hardcodedContentTypes[normalizedModelName];
        contentTypeIdCache.set(normalizedModelName, hardcodedId);
        return hardcodedId;
      }
      
      // If still not found, throw error
      throw new Error(`Content type not found for model: ${normalizedModelName}`);
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      throw new Error(`Unable to get content type for model: ${normalizedModelName}`);
    }
  }
  })();

  contentTypeIdInFlight.set(normalizedModelName, request);
  try {
    return await request;
  } finally {
    if (contentTypeIdInFlight.get(normalizedModelName) === request) {
      contentTypeIdInFlight.delete(normalizedModelName);
    }
  }
};

// Get all ContentTypes
export const getAllContentTypes = async (): Promise<ContentType[]> => {
  try {
    const response = await axiosInstance.get('documents/contenttypes/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get common ContentTypes
export const getCommonContentTypes = async (): Promise<ContentType[]> => {
  try {
    const response = await axiosInstance.get('documents/contenttypes/common/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get documents for an object
export const getDocumentsByObject = async (
  contentTypeId: number, 
  objectId: string, 
  filters?: {
    documentType?: string;
    uploadedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }
): Promise<Document[]> => {
  try {
    const params = new URLSearchParams();
    params.append('content_type_id', contentTypeId.toString());
    params.append('object_id', objectId);
    
    if (filters?.documentType) {
      params.append('document_type', filters.documentType);
    }
    if (filters?.uploadedBy) {
      params.append('uploaded_by', filters.uploadedBy);
    }
    if (filters?.dateFrom) {
      params.append('date_from', filters.dateFrom);
    }
    if (filters?.dateTo) {
      params.append('date_to', filters.dateTo);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    
    const response = await axiosInstance.get(`documents/?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get documents in a folder
export const getDocumentsByFolder = async (
  contentTypeId: number, 
  objectId: string, 
  folderId: string
): Promise<Document[]> => {
  try {
    const response = await axiosInstance.get(
      `documents/by-object/${contentTypeId}/${objectId}/folder/${folderId}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Upload document
export const uploadDocument = async (documentData: CreateDocumentRequest): Promise<Document> => {
  try {
    const formData = new FormData();
    formData.append('content_type_id', documentData.content_type_id.toString());
    formData.append('object_id', documentData.object_id);
    formData.append('name', documentData.name);
    formData.append('mime_type', documentData.mime_type || '');
    formData.append('is_directory', (documentData.is_directory || false).toString());
    
    if (documentData.file) {
      formData.append('file', documentData.file);
    }
    
    if (documentData.parent_id) {
      formData.append('parent_id', documentData.parent_id);
    }
    
    // Only append document_type_id if it's a valid UUID
    if (documentData.document_type_id && documentData.document_type_id !== 'default-1') {
      // Check if it's a valid UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(documentData.document_type_id)) {
        formData.append('document_type_id', documentData.document_type_id);
      }
    }

    const response = await axiosInstance.post('documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create folder
export const createFolder = async (folderData: CreateFolderRequest): Promise<Document> => {
  try {
    const response = await axiosInstance.post('documents/create-folder/', folderData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete document
export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`documents/${documentId}/`);
  } catch (error) {
    throw error;
  }
};

// Rename document
export const renameDocument = async (documentId: string, newName: string): Promise<Document> => {
  try {
    const response = await axiosInstance.post(`documents/${documentId}/rename/`, {
      name: newName,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Move document
export const moveDocument = async (documentId: string, newParentId?: string): Promise<Document> => {
  try {
    const response = await axiosInstance.post(`documents/${documentId}/move/`, {
      parent_id: newParentId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get download URL
export const getDownloadUrl = async (documentId: string): Promise<{ download_url: string }> => {
  try {
    const response = await axiosInstance.get(`documents/${documentId}/download/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Convenience functions for Order documents
export const getOrderDocuments = async (
  orderId: string, 
  filters?: {
    documentType?: string;
    uploadedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }
): Promise<Document[]> => {
  const contentTypeId = await getContentTypeId('order');
  return getDocumentsByObject(contentTypeId, orderId, filters);
};

export const uploadOrderDocument = async (orderId: string, file: File, name?: string, documentTypeId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('order');
  return uploadDocument({
    content_type_id: contentTypeId,
    object_id: orderId,
    name: name || file.name,
    file: file,
    mime_type: file.type,
    is_directory: false,
    document_type_id: documentTypeId,
  });
};

export const uploadTransactionDocument = async (transactionId: string, file: File, name?: string, documentTypeId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('transaction');
  return uploadDocument({
    content_type_id: contentTypeId,
    object_id: transactionId,
    name: name || file.name,
    file: file,
    mime_type: file.type,
    is_directory: false,
    document_type_id: documentTypeId,
  });
};

export const getTaskDocuments = async (
  taskId: string,
  filters?: {
    documentType?: string;
    uploadedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }
): Promise<Document[]> => {
  const contentTypeId = await getContentTypeId('task');
  return getDocumentsByObject(contentTypeId, taskId, filters);
};

export const uploadTaskDocument = async (taskId: string, file: File, name?: string, documentTypeId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('task');
  return uploadDocument({
    content_type_id: contentTypeId,
    object_id: taskId,
    name: name || file.name,
    file: file,
    mime_type: file.type,
    is_directory: false,
    document_type_id: documentTypeId,
  });
};

export const createOrderFolder = async (orderId: string, name: string, parentId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('order');
  
  const folderData: any = {
    content_type_id: contentTypeId,
    object_id: orderId,
    name: name,
  };
  
  // Only add parent_id if it's provided
  if (parentId) {
    folderData.parent_id = parentId;
  }
  
  return createFolder(folderData);
};

// Convenience functions for Customer documents
export const getCustomerDocuments = async (customerId: string): Promise<Document[]> => {
  const contentTypeId = await getContentTypeId('customer');
  return getDocumentsByObject(contentTypeId, customerId);
};

export const uploadCustomerDocument = async (customerId: string, file: File, name?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('customer');
  return uploadDocument({
    content_type_id: contentTypeId,
    object_id: customerId,
    name: name || file.name,
    file: file,
    mime_type: file.type,
    is_directory: false,
  });
};

export const createCustomerFolder = async (customerId: string, name: string, parentId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('customer');
  return createFolder({
    content_type_id: contentTypeId,
    object_id: customerId,
    name: name,
    parent_id: parentId,
  });
};

// Convenience functions for Company documents
export const getCompanyDocuments = async (companyId: string): Promise<Document[]> => {
  const contentTypeId = await getContentTypeId('company');
  return getDocumentsByObject(contentTypeId, companyId);
};

export const uploadCompanyDocument = async (companyId: string, file: File, name?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('company');
  return uploadDocument({
    content_type_id: contentTypeId,
    object_id: companyId,
    name: name || file.name,
    file: file,
    mime_type: file.type,
    is_directory: false,
  });
};

export const createCompanyFolder = async (companyId: string, name: string, parentId?: string): Promise<Document> => {
  const contentTypeId = await getContentTypeId('company');
  return createFolder({
    content_type_id: contentTypeId,
    object_id: companyId,
    name: name,
    parent_id: parentId,
  });
};
